import React, { createContext, useState, useCallback, useContext, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { AuthenticationContext } from "../authentication/authentication.context";

export const DropdownDataContext = createContext();

export const DropdownDataContextProvider = ({ children }) => {
  // State for all dropdown data
  const [dropdownData, setDropdownData] = useState({
    teams: [],
    users: [],
    branchLocations: [],
    listForDropDown: [],
    isLoading: false,
    isReady: false,
    lastFetchTime: null,
    error: null
  });

  // Get authentication context
  const { currentUser } = useContext(AuthenticationContext);

  // Fetch teams data
  const fetchTeams = useCallback(async () => {
    try {
      const operatorId = Cookies.get("userId");
      if (!operatorId) {
        console.error('[DROPDOWN DATA] No operatorId found in cookies');
        return [];
      }
      
      const response = await axios.get(
          (process.env.REACT_APP_KNOWBIE_API_URL || "https://knowbie-back-dev-ff38cc203ae3.herokuapp.com/api/")
        + "/teams/get-teams",
        {
          params: { operatorId },
          headers: { Authorization: `Bearer ${Cookies.get("token")}` },
        }
      );
      
      return response.data.teams || [];
    } catch (error) {
      return [];
    }
  }, []);

  // Fetch users data
  const fetchUsers = useCallback(async (operatorId) => {
    try {
      const response = await axios.post('/api/server/get-all-servers', { operatorId });
      
      return response.data.allServers || [];
    } catch (error) {
      return [];
    }
  }, []);

  // Fetch branch locations data
  const fetchBranchLocations = useCallback(async (currentUser, teams) => {
    try {
      if (!currentUser) {
        return [];
      }

      const operatorIds = currentUser["manages"] || [];
      const fetchedLocations = [];
      let knowbieServers = [];
      const knowbieOperatorId = currentUser._id;

      // Sort teams by team_owner_id
      const teamsByOwner = {};
      teams.forEach((team) => {
        const ownerId = team.team_owner_id;
        if (ownerId) {
          if (!teamsByOwner[ownerId]) {
            teamsByOwner[ownerId] = [];
          }
          teamsByOwner[ownerId].push({
            ...team,
            team_name: team.team_name,
            _id: team._id
          });
        }
      });

      // Fetch data for each managed operator
      for (const operatorId of operatorIds) {
        try {
          // Get operator details
          const operatorResponse = await axios.get(`/api/user/get-operator-v3/${operatorId}`);
          let organizationName = "Organization";
          
          if (operatorResponse.data && operatorResponse.data.user) {
            const operatorData = operatorResponse.data.user;
            organizationName = operatorData["operator/organization"] || operatorData["operator/name"] || "Organization";
          }

          // Get teams for this operator
          const operatorTeams = teamsByOwner[operatorId] || [];

          // Get servers for this operator
          const serversResponse = await axios.post('/api/server/get-all-servers', { operatorId });
          
          if (serversResponse.data && serversResponse.data.allServers) {
            const servers = serversResponse.data.allServers.map(server => ({
              id: server._id,
              name: `${server.first_name || ''} ${server.last_name || ''}`.trim(),
              first_name: server.first_name,
              last_name: server.last_name,
              type: "user"  // Ensure external operator users have the correct type
            }));

            // Special handling for current user's organization
            if (operatorId === knowbieOperatorId) {
              knowbieServers = servers;
              continue;
            }

            // Create location object
            const branchLocation = {
              id: operatorId,
              name: organizationName,
              apiName: true,
              servers: servers,
              teams: operatorTeams
            };

            fetchedLocations.push(branchLocation);
          }
        } catch (err) {
          // Add fallback location
          const operatorTeams = teamsByOwner[operatorId] || [];
          const fallbackLocation = {
            id: operatorId,
            name: "Unknown Organization",
            apiName: true,
            servers: [],
            teams: operatorTeams
          };
          fetchedLocations.push(fallbackLocation);
        }
      }

      // Add Knowbie Restaurant with current user's servers
      if (knowbieServers.length > 0) {
        const knowbieRestaurant = {
          id: knowbieOperatorId,
          name: currentUser["operator/organization"] || "My Restaurant",
          apiName: true,
          servers: knowbieServers,
          teams: teamsByOwner[knowbieOperatorId] || []
        };
        fetchedLocations.unshift(knowbieRestaurant);
      }

      return fetchedLocations;
    } catch (error) {
      return [];
    }
  }, []);

  // Create listForDropDown format
  const createListForDropDown = useCallback((teams, users) => {
    const mappedTeams = teams.map((team) => ({
      type: "team",
      name: team.team_name,
      id: team._id,
      team_owner_id: team.team_owner_id,
    }));

    const mappedUsers = users.map((user) => ({
      type: "user",
      name: `${user.first_name} ${user.last_name}`,
      id: user._id,
    }));

    return [...mappedTeams, ...mappedUsers];
  }, []);

  // Main function to load all dropdown data
  const loadAllDropdownData = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    
    setDropdownData(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      // Fetch all data in parallel for better performance
      const [teams, users] = await Promise.all([
        fetchTeams(),
        fetchUsers(currentUser._id)
      ]);

      // Fetch branch locations with teams data
      const branchLocations = await fetchBranchLocations(currentUser, teams);

      // Create listForDropDown format
      const listForDropDown = createListForDropDown(teams, users);

      // Update state with all data
      setDropdownData({
        teams,
        users,
        branchLocations,
        listForDropDown,
        isLoading: false,
        isReady: true,
        lastFetchTime: new Date(),
        error: null
      });

    } catch (error) {
      // Only update state if there's no error or if it's a network error
      if (error.name !== 'AbortError') {
        setDropdownData(prev => ({
          ...prev,
          isLoading: false,
          error: error.message || 'Failed to load dropdown data'
        }));
      }
    }
  }, [currentUser, fetchTeams, fetchUsers, fetchBranchLocations, createListForDropDown]);

  // Auto-load data when user is authenticated
  useEffect(() => {
    let isMounted = true;
    
    if (currentUser && !dropdownData.isReady && !dropdownData.isLoading) {
      loadAllDropdownData().then(() => {
        // Only update if still mounted
        if (!isMounted) {
        }
      });
    }
    
    return () => {
      isMounted = false;
    };
  }, [currentUser, dropdownData.isReady, dropdownData.isLoading, loadAllDropdownData]);

  // Refresh function for manual updates
  const refreshDropdownData = useCallback(() => {
    loadAllDropdownData();
  }, [loadAllDropdownData]);

  // Function to check if data is stale (older than 5 minutes)
  const isDataStale = useCallback(() => {
    if (!dropdownData.lastFetchTime) return true;
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return dropdownData.lastFetchTime < fiveMinutesAgo;
  }, [dropdownData.lastFetchTime]);

  // Auto-refresh stale data
  useEffect(() => {
    let isMounted = true;
    
    if (dropdownData.isReady && isDataStale() && currentUser && isMounted) {
      loadAllDropdownData().then(() => {
        // Only update if still mounted
        if (!isMounted) {
        }
      });
    }
    
    return () => {
      isMounted = false;
    };
  }, [dropdownData.isReady, isDataStale, currentUser, loadAllDropdownData]);

  const contextValue = {
    // Data
    teams: dropdownData.teams,
    users: dropdownData.users,
    branchLocations: dropdownData.branchLocations,
    listForDropDown: dropdownData.listForDropDown,
    
    // Status
    isLoading: dropdownData.isLoading,
    isReady: dropdownData.isReady,
    error: dropdownData.error,
    lastFetchTime: dropdownData.lastFetchTime,
    
    // Actions
    refreshDropdownData,
    loadAllDropdownData,
    isDataStale: isDataStale()
  };

  return (
    <DropdownDataContext.Provider value={contextValue}>
      {children}
    </DropdownDataContext.Provider>
  );
}; 